import React, { Component } from 'react';
import { $state, $stateParams, UISref } from '../../router';
import { Request, Auth, Challenge, TokenManager, AmazonCognitoAdvancedSecurityData } from '../../services';
import { APP_CONST } from '../../constants';
import {
    GeckoAlert,
    GeckoBtn,
    GeckoCardFooter,
    GeckoCardWrapper,
    GeckoCardBody,
    GeckoFields,
    GeckoLogoHeader,
    PasswordPolicy,
} from '../../components';
import { titles as password_policy_titles } from '../../components/PasswordPolicy/policy';
import ErrorTracker from '../../services/ErrorTracker';
import Redirect from '../../services/Redirect';

// Styles
import './styles.css';
import Captcha from '../../services/Captcha';
import Flash from '../../services/Flash';

// Constants
export const STATE_LOGIN = 'STATE_LOGIN';
export const STATE_LOGIN_WITH_PASSWORD = 'STATE_LOGIN_WITH_PASSWORD';
export const STATE_SET_PASSWORD = 'STATE_SET_PASSWORD';
export const STATE_RESET_PASSWORD = 'STATE_RESET_PASSWORD';
export const STATE_CONFIRM_FORGOT_PASSWORD = 'STATE_CONFIRM_FORGOT_PASSWORD';
export const STATE_SELECT_MFA_TYPE = 'STATE_SELECT_MFA_TYPE';
export const STATE_SMS_MFA = 'STATE_SMS_MFA';
export const STATE_SOFTWARE_TOKEN_MFA = 'STATE_SOFTWARE_TOKEN_MFA';
export const DEFAULT_PASSWORD_LABEL = 'Password';

const AUTH_STATES_PRIORITY = [
    STATE_LOGIN,
    STATE_LOGIN_WITH_PASSWORD,
    STATE_SET_PASSWORD,
    STATE_RESET_PASSWORD,
    STATE_CONFIRM_FORGOT_PASSWORD,
    STATE_SELECT_MFA_TYPE,
    STATE_SMS_MFA,
    STATE_SOFTWARE_TOKEN_MFA,
];

export const CLASSNAME_HIDE_PASSWORD_FIELD = 'hide-password-field';
export const CLASSNAME_SHOW_PASSWORD_FIELD = 'animated flipInX';
export const CLASSNAME_ANIMATED_FORM = 'animated shake';

const INITIAL_STATE = {
    shakeableClass: '',
    authState: STATE_LOGIN,
    error: null,
    message: null,
    email: '',
    password: '',
    advancedSecurityData: null,
    mfaCode: null,
    username: null,
    mfaOptions: [],
    mfaMethod: null,
    passwordLabel: DEFAULT_PASSWORD_LABEL,
};

export class LoginPage extends Component {

    componentDidMount() {
        this.fillFromStateParams();
        this.flashMessage();
    }

    fillFromStateParams() {
        let newParams = {
            email: $stateParams.email || '',
            passwordLabel: $stateParams.password === 'temporary' ? 'Temporary Password' : DEFAULT_PASSWORD_LABEL,
        };

        if (newParams.email) {
            this.setState(newParams, this.login);
        } else if (newParams.passwordLabel) {
            this.setState(newParams);
        }
    }

    flashMessage() {
        Flash.get(APP_CONST.FLASH.SUCCESSFUL_PASSWORD_CHANGE).then((value) => {
            if (value) {
                this.setState({
                    message: 'Your password has been changed',
                });
            }
        });
    }

    state = {
        ...INITIAL_STATE,
        // authState: STATE_SELECT_MFA_TYPE,
    };

    handleChange(name, evt) {
        this.setState({ [name]: evt });
    }

    /**
     * Animate the form by shaking it. This should be called if the user
     * makes an error or an error is returned from the API
     *
     */
    shakeForm() {
        this.setState({
            shakeableClass: CLASSNAME_ANIMATED_FORM,
        }, () => {
            setTimeout(() => {
                this.setState({
                    shakeableClass: '',
                })
            }, 400)
        });
    }

    /**
     * The authState keeps track of which point in the authentication
     * process that the user is at. This also controls which fields
     * are shown to the user.
     *
     * @return void
     */
    setAuthState(nextState) {
        if (AUTH_STATES_PRIORITY.includes(nextState)) {
            this.setState({
                authState: nextState,
                error: null,
                message: null,
            }, () => {
                this.authStateDidChange(nextState);
            });
        }
    }

    authStateDidChange(newState) {
        // This is cosmetic only so if any of these
        // fail it should not stop the user
        try {
            switch(newState) {
                case STATE_LOGIN_WITH_PASSWORD:
                    this.passwordRef.focus();
                break;

                case STATE_SOFTWARE_TOKEN_MFA:
                    this.softwareMfaRef.focus();
                break;

                case STATE_SMS_MFA:
                    this.smsMfaRef.focus();
                break;

                default:
                    break;
            }
        } catch(err) {
            console.error('Failed to autofocus', err);
        }
    }

    login() {
        // Simple reset to clean up
        this.setState({
            error: null,
            message: null,
        });

        switch (this.state.authState) {
            case STATE_LOGIN_WITH_PASSWORD:
                this.respond(Challenge.USER_PASSWORD, {
                    Username: this.state.email.trim(),
                    Password: this.state.password,
                    AdvancedSecurity: this.state.advancedSecurityData
                });
                break;

            case STATE_SOFTWARE_TOKEN_MFA:
            case STATE_SMS_MFA:

                try {
                    const challengeName = this.state.authState === STATE_SMS_MFA ? Challenge.SMS_MFA : Challenge.SOFTWARE_TOKEN_MFA;

                    this.respond(challengeName, {
                        MfaCode: this.state.mfa_code,
                        Session: this.state.session,
                        Username: this.state.username
                    });
                } catch (err) {
                    this.shakeForm();
                    this.setState({
                        error: err.message,
                    });
                };
                break;

            case STATE_SELECT_MFA_TYPE:

                try {
                    this.respond(Challenge.SELECT_MFA_TYPE, {
                        MfaMethod: this.state.mfaMethod,
                        Session: this.state.session,
                        Username: this.state.username
                    });
                } catch (err) {
                    this.shakeForm();
                    this.setState({
                        error: err.message,
                    });
                };
                break;

            case STATE_LOGIN:
                this.loginWithEmail();
                break;

            default:
                break;
        }
    }


    /**
     * Reset the users password. If the state is STATE_CONFIRM_FORGOT_PASSWORD then
     * the user has already requested a reset code and should have provided a new
     * password to be validated and changed.
     *
     * If the state is not STATE_CONFIRM_FORGOT_PASSWORD then the user has not yet
     * requested a password reset code and should have provided the email address
     * they want to reset.
     *
     * @return void
     */
    reset() {
        // Simple reset to clean up
        this.setState({
            error: null,
            message: null,
        });

        switch (this.state.authState) {
            case STATE_CONFIRM_FORGOT_PASSWORD:
                try {
                    this.validateNewPassword(this.state.new_password, this.state.confirm_new_password, this.state.password_reset_code);

                    return Captcha.getPayload('confirm_forgot_password').then((captchaPayload) => {
                        this.respond(Challenge.CONFIRM_FORGOT_PASSWORD, {
                            Username: this.state.email.trim(),
                            ConfirmationCode: this.state.password_reset_code,
                            NewPassword: this.state.new_password,
                            Session: this.state.session,
                        }, {
                            captcha: captchaPayload,
                        });
                    });

                } catch (err) {
                    this.shakeForm();

                    this.setState({
                        error: err.message,
                    });
                }
                break;

            case STATE_SET_PASSWORD:

                try {
                    this.validateUpdatePassword(this.state.new_password, this.state.confirm_new_password);

                    this.respond(Challenge.NEW_PASSWORD_REQUIRED, {
                        Username: this.state.email.trim(),
                        Password: this.state.new_password,
                        Session: this.state.session,
                    });
                } catch (err) {
                    this.shakeForm();
                    this.setState({
                        error: err.message,
                    });
                };
                break;

            default:
                this.resetWithEmailAddress();
                break;
        }

    }

    /**
     * Request password reset email for email address
     *
     */
    resetWithEmailAddress() {
        return Auth.resetPassword(this.state.email.trim())
            .then((data) => this.handleWithChallenge(data))
            .catch(err => {
                let error = err.Error;
                this.setState({ error });
            });
    }

    /**
     * Perform an inital login request passing the email address.
     * This will likely respond with a a challenge which will request
     * and email address, MFA code or SSO url
     */
    loginWithEmail() {
        Auth.init(this.state.email.trim())
            .then((data) => {
                // set user as soon as we know an email so we can track
                // errors even if the user can't complete their login
                ErrorTracker.setUser(null, this.state.email.trim());
                return data;
            })
            .then((data) => this.handleResponse(data))
            .catch(err => this.setState({ error: err.Error }));
    }

    /**
     * Complete a challenge, likely providing a password to
     * accompany the initial email address
     *
     * @param string ChallengeName - one of Challenge.CHALLENGE_NAMES
     * @param mixed ChallengeResponse - the value to respond to the challenge
     *
     * @return void
     */
    respond(ChallengeName, ChallengeValue, otherParams = {}) {
        Auth.respond(ChallengeName, ChallengeValue, otherParams)
            .then((data) => this.handleResponse(data))
            .catch(err => {
                this.shakeForm();
                this.setState({ error: err.Error })
            });
    }

    /**
     * Determin how to handle the network response. This will
     * either begin a challenge or complete the authentication
     * with tokens
     *
     * @param data - network response
     *
     * @return void
     */
    handleResponse(data) {
        if (data.ChallengeName) {
            this.handleWithChallenge(data);
        } else if (data.IdToken) {
            this.handleWithToken(data);
        }
    }

    /**
     * API requires further information so begin a Challenge
     *
     * @param data - network response
     *
     * @return void
     */
    handleWithChallenge(data) {
        return Challenge.fromResponse(data)
            .handle()
            .then(({ challenge_action, session, message, error, advanced_security }) => {
                if (advanced_security) {
                    var advancedSecurityData = AmazonCognitoAdvancedSecurityData.getData(
                        advanced_security.UserId,
                        advanced_security.PoolId,
                        advanced_security.ClientId
                    );

                    this.setState({
                        advancedSecurityData: advancedSecurityData
                    });
                }

                switch (challenge_action) {
                    case Challenge.USER_PASSWORD:
                        this.setAuthState(STATE_LOGIN_WITH_PASSWORD);
                        this.setState({
                            message: message,
                        });
                        break;

                    case Challenge.NEW_PASSWORD_REQUIRED:
                        this.setAuthState(STATE_SET_PASSWORD);
                        // The API doesnt alwasy return this so dont overwrite if null
                        if (session) {
                            this.setState({
                                session: session,
                            });
                        }
                        break;

                    case Challenge.CONFIRM_FORGOT_PASSWORD:
                        this.setAuthState(STATE_CONFIRM_FORGOT_PASSWORD);

                        this.setState({
                            message: message,
                            resetSent: true,
                        });
                        break;

                    case Challenge.SELECT_MFA_TYPE:
                        this.setAuthState(STATE_SELECT_MFA_TYPE);

                        this.setState({
                            session: session,
                            username: data.ChallengeValue.USER_ID_FOR_SRP,
                            mfaOptions: JSON.parse(data.ChallengeValue.MFAS_CAN_CHOOSE),
                        });
                        break;

                    case Challenge.SMS_MFA:
                        this.setAuthState(STATE_SMS_MFA);

                        this.setState({
                            session: session,
                            username: data.ChallengeValue.USER_ID_FOR_SRP,
                            mfaDestination: data.ChallengeValue.CODE_DELIVERY_DESTINATION
                        });
                        break;

                    case Challenge.SOFTWARE_TOKEN_MFA:
                        this.setAuthState(STATE_SOFTWARE_TOKEN_MFA);

                        this.setState({
                            session: session,
                            username: data.ChallengeValue.USER_ID_FOR_SRP
                        });
                        break;

                    default:
                        break;
                }

                // Put this at the end so setAuthState() doesnt override the error message
                if (error) {
                    this.shakeForm();
                    this.setState({
                        error: error,
                    });
                }
            });
    }

    /**
     * The user has successfully logged in so persist the tokens
     *
     * @param data - network response
     *
     * @return void
     */
    handleWithToken(data) {
        const tm = TokenManager.fromResponse(data);
        tm.persistToLocalStorage();

        const idToken = TokenManager.decode(tm.IdToken);
        const accessToken = TokenManager.decode(tm.AccessToken);
        ErrorTracker.setUser(accessToken.username, idToken.email);

        if (Redirect.hasRedirect()) {
            window.location = Redirect.get();
        } else {
            $state.go('selectprofile');
        }
    }

    /**
     * Validate the password reset by confirming the new password
     * We could also implement password requirements here as well
     *
     * @return bool - is valid or nahhhh
     */
    validateNewPassword(new_password = '', confirm_new_password = '', password_reset_code = '') {
        if (new_password !== confirm_new_password) {
            throw new Error('Passwords do not match');
        }

        const policy_results = PasswordPolicy.runAllValidators(new_password);
        Object.keys(policy_results).map((key) => {
            if (!policy_results[key] && password_policy_titles[key]) {
                throw new Error(`Password must have ${password_policy_titles[key].toLowerCase()}`);
            }
        });

        if (!password_reset_code || password_reset_code === '') {
            throw new Error('Provide the password reset code emailed to you');
        }

        if (password_reset_code.length < 6) {
            throw new Error('Reset code should be at least 6 characters');
        }

        return true;
    }


    validateUpdatePassword(new_password, confirm_new_password) {
        if (!new_password || !confirm_new_password) {
            throw new Error('Please provide a new password and confirm your new password');
        }

        if (new_password !== confirm_new_password) {
            throw new Error('Passwords do not match');
        }

        if (!PasswordPolicy.every(new_password)) {
            throw new Error('Password not strong enough');
        }

        return true;
    }

    /**
     * Once the user enters an email address the email field is disabled
     * as we we are not tied to a cognito session id and already checked
     * that the user requires a password etc
     *
     * This can be an issue in iframes or webviews where reloading the page
     * is invonvienient therefore lets give the user a button to reset the
     * login
     *
     */
    renderEscapeRope() {
        if (!this.state.email || this.state.authState === STATE_LOGIN) {
            return null;
        }

        return (
            <button className="escapeRope" onClick={() => this.setState(INITIAL_STATE)}>Login with different username</button>
        )
    }

    renderErrorAlert() {
        if (this.state.error) {
            return (
                <div style={{ marginBottom: '15px', textAlign: 'center' }}>
                    <GeckoAlert type={APP_CONST.ALERT_TYPE_DANGER}>
                        {this.state.error}
                    </GeckoAlert>
                </div>
            );

        }
        return null;
    }

    renderMessageAlert() {
        if (this.state.message) {
            return (
                <div style={{ marginBottom: '15px', textAlign: 'center' }}>
                    <GeckoAlert type={APP_CONST.ALERT_TYPE_INFO}>
                        {this.state.message}
                    </GeckoAlert>
                </div>
            );

        }
        return null;
    }

    renderForgotPassword() {
        if (this.state.authState === STATE_LOGIN_WITH_PASSWORD) {
            return (
                <button className="forgot-password pull-left" onClick={() => { this.setAuthState(STATE_RESET_PASSWORD) }}>
                    Forgot password?
                </button>
            );
        }
        return null;
    }

    fields(password_classname = '') {
        return [{
            label: 'Email Address',
            key: 'email',
            type: APP_CONST.FIELD_TYPE_EMAIL,
            width: '100%',
            disabledWhen: () => {
                return [STATE_LOGIN_WITH_PASSWORD, STATE_CONFIRM_FORGOT_PASSWORD, STATE_SET_PASSWORD].includes(this.state.authState);
            },
            hideWhen: () => {
                return ![STATE_LOGIN, STATE_LOGIN_WITH_PASSWORD, STATE_RESET_PASSWORD, STATE_CONFIRM_FORGOT_PASSWORD, STATE_SET_PASSWORD].includes(this.state.authState);
            },
            attributes: {
                autoFocus: true,
                autoComplete: "off",
            }
        }, {
            ref: (ref) => this.passwordRef = ref,
            label: this.state.passwordLabel,
            key: 'password',
            className: password_classname,
            name: 'password',
            width: '100%',
            type: APP_CONST.FIELD_TYPE_PASSWORD,
            hideWhen: () => false,
            attributes: {
                autoComplete: 'current-password',
                autoFocus: true,
            }
        }, {
            label: 'New Password',
            key: 'new_password',
            width: '100%',
            type: APP_CONST.FIELD_TYPE_PASSWORD,
            hideWhen: () => {
                return ![STATE_SET_PASSWORD, STATE_CONFIRM_FORGOT_PASSWORD].includes(this.state.authState);
            },
            attributes: {
                autoComplete: 'new-password',
            },
        }, {
            key: 'password_policy',
            render: () => {
                return (
                    <div className="PasswordPolicyContainer">
                        <PasswordPolicy value={this.state.new_password} />
                    </div>
                );
            },
            hideWhen: () => {
                return ![STATE_SET_PASSWORD, STATE_CONFIRM_FORGOT_PASSWORD].includes(this.state.authState);
            },
        }, {
            label: 'Confirm New Password',
            key: 'confirm_new_password',
            width: '100%',
            type: APP_CONST.FIELD_TYPE_PASSWORD,
            hideWhen: () => {
                return ![STATE_SET_PASSWORD, STATE_CONFIRM_FORGOT_PASSWORD].includes(this.state.authState);
            },
            attributes: {
                autoComplete: 'new-password',
            },
        }, {
            label: 'Verification Code',
            key: 'password_reset_code',
            description: 'A verification code has been sent to you',
            width: '100%',
            hideWhen: () => this.state.authState !== STATE_CONFIRM_FORGOT_PASSWORD
        }, {
            label: 'SMS MFA Code',
            key: 'mfa_code',
            ref: (ref) => this.smsMfaRef = ref,
            description: 'An MFA code has been sent to ' + this.state.mfaDestination,
            width: '100%',
            hideWhen: () => this.state.authState !== STATE_SMS_MFA,
            attributes: {
                autoFocus: true,
            }
        }, {
            label: 'Software MFA Code',
            key: 'mfa_code',
            ref: (ref) => this.softwareMfaRef = ref,
            description: 'Please enter the code from your MFA app',
            width: '100%',
            hideWhen: () => this.state.authState !== STATE_SOFTWARE_TOKEN_MFA,
            attributes: {
                autoFocus: true,
            }
        }, {
            label: 'Select your MFA method',
            key: 'mfa_method',
            description: 'How would you like to proceed',
            render: () => {
                return (
                    <div>
                        <div className="preferredMfaLoginLabel">Select Preferred MFA Method</div>

                        {this.state.mfaOptions.map(function (name, index) {
                            return (
                                <div key={index} className="d-flex flex-row justify-content-start align-items-center">
                                    <input type="radio"
                                        style={{display: 'flex', marginRight: '10px', width: 'auto'}}
                                        id={name}
                                        name="mfa_method"
                                        value={name}
                                        checked={this.state.mfaMethod === name}
                                        onChange={this.onMfaMethodChanged.bind(this)} />
                                    <label style={{marginBottom: 0}} htmlFor={name}>
                                        {this.mfaPrettyName(name)}
                                    </label>
                                </div>
                            );
                        }.bind(this))}

                    </div>
                );
            },
            hideWhen: () => this.state.authState !== STATE_SELECT_MFA_TYPE
        },];
    }

    mfaPrettyName(name) {
        switch(name) {
            case 'SMS_MFA':
                return 'SMS';
            case 'SOFTWARE_TOKEN_MFA':
                return 'Software';
            default:
                return name;
        }
    }

    onMfaMethodChanged(event) {
        this.setState({
            mfaMethod: event.target.value
        });
    }

    render() {
        let password_classname = makePasswordClassName(this.state.authState);

        const loginBtn = {
            id: 'login',
            name: 'Login',
            type: APP_CONST.BTN_TYPE_PRIMARY,
            icon: 'sign-in-alt',
            className: 'float-right',
            handler: this.login.bind(this),
            hideWhen: () => [STATE_SET_PASSWORD, STATE_RESET_PASSWORD, STATE_CONFIRM_FORGOT_PASSWORD].includes(this.state.authState),
        };

        const updatePasswordButton = {
            id: 'update-password',
            name: 'Update Password',
            type: APP_CONST.BTN_TYPE_PRIMARY,
            className: 'float-right',
            handler: this.reset.bind(this),
            hideWhen: () => ![STATE_SET_PASSWORD].includes(this.state.authState),
        };

        const resetPasswordBtn = {
            name: 'Reset Password',
            id: 'reset-password',
            icon: 'unlock-alt',
            className: 'float-right',
            type: APP_CONST.BTN_TYPE_PRIMARY,
            handler: this.reset.bind(this),
            hideWhen: () => ![STATE_RESET_PASSWORD, STATE_CONFIRM_FORGOT_PASSWORD].includes(this.state.authState),
        };

        return (
            <div className={this.state.shakeableClass}>
                <GeckoCardWrapper className="z-depth-2 animated fadeIn">


                    <GeckoCardBody padded={true}>
                        {this.renderErrorAlert()}
                        {this.renderMessageAlert()}

                        <GeckoFields
                            fields={this.fields(password_classname)}
                            values={this.state}
                            handler={this.handleChange.bind(this)}
                            submitHandler={this.login.bind(this)} />
                    </GeckoCardBody>
                    <GeckoCardFooter>
                        {this.renderForgotPassword()}
                        <GeckoBtn {...loginBtn}></GeckoBtn>
                        <GeckoBtn {...resetPasswordBtn}></GeckoBtn>
                        <GeckoBtn {...updatePasswordButton}></GeckoBtn>
                    </GeckoCardFooter>
                </GeckoCardWrapper>

                {this.renderEscapeRope()}
            </div>
        );
    }

}

export function makePasswordClassName(authState) {
    return (authState === STATE_LOGIN_WITH_PASSWORD ? CLASSNAME_SHOW_PASSWORD_FIELD : CLASSNAME_HIDE_PASSWORD_FIELD);
}

export default LoginPage;
